import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Calculator, TrendingUp, Info, AlertTriangle, CreditCard } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, RadialBarChart, RadialBar, LineChart, Line } from 'recharts';

const STORAGE_KEY = 'loan_eligibility_data';

const LoanEligibilityCalculator = () => {
  const [monthlyIncome, setMonthlyIncome] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).monthlyIncome : 100000;
  });
  
  const [existingEMI, setExistingEMI] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).existingEMI : 15000;
  });
  
  const [age, setAge] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).age : 30;
  });
  
  const [interestRate, setInterestRate] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).interestRate : 9.5;
  });
  
  const [loanTenure, setLoanTenure] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).loanTenure : 20;
  });

  const [otherIncome, setOtherIncome] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).otherIncome : 0;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      monthlyIncome,
      existingEMI,
      age,
      interestRate,
      loanTenure,
      otherIncome
    }));
  }, [monthlyIncome, existingEMI, age, interestRate, loanTenure, otherIncome]);

  const calculateEligibility = () => {
    const income = parseFloat(monthlyIncome) || 0;
    const other = parseFloat(otherIncome) || 0;
    const existing = parseFloat(existingEMI) || 0;
    const rate = parseFloat(interestRate) || 0;
    const tenure = parseFloat(loanTenure) || 0;
    const currentAge = parseInt(age) || 0;

    const totalIncome = income + other;
    
    // FOIR (Fixed Obligation to Income Ratio) - typically 50-60% for salaried
    const maxFOIR = 0.55;
    const availableForEMI = totalIncome * maxFOIR;
    const maxEMI = availableForEMI - existing;

    // Calculate max loan amount based on max EMI
    const monthlyRate = rate / 12 / 100;
    const numPayments = tenure * 12;
    
    let maxLoanAmount = 0;
    if (monthlyRate > 0 && numPayments > 0 && maxEMI > 0) {
      maxLoanAmount = maxEMI * (Math.pow(1 + monthlyRate, numPayments) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, numPayments));
    }

    // DTI (Debt-to-Income) ratio
    const dtiRatio = totalIncome > 0 ? (existing / totalIncome) * 100 : 0;
    const foirRatio = totalIncome > 0 ? ((existing + maxEMI) / totalIncome) * 100 : 0;

    // Age-based eligibility
    const maxAge = 65; // Most banks
    const remainingYears = maxAge - currentAge;
    const maxTenureByAge = Math.min(tenure, remainingYears);

    // Total interest calculation
    const totalPayment = maxEMI * numPayments;
    const totalInterest = totalPayment - maxLoanAmount;

    return {
      maxLoanAmount,
      maxEMI,
      availableForEMI,
      dtiRatio,
      foirRatio,
      maxTenureByAge,
      totalInterest,
      totalPayment,
      disposableIncome: totalIncome - existing - maxEMI
    };
  };

  const generateIncomeEMIData = () => {
    const results = calculateEligibility();
    const income = parseFloat(monthlyIncome) || 0;
    const other = parseFloat(otherIncome) || 0;
    const existing = parseFloat(existingEMI) || 0;
    const totalIncome = income + other;

    return [
      { name: 'Salary Income', value: income },
      { name: 'Other Income', value: other },
      { name: 'Existing EMI', value: existing },
      { name: 'New EMI Capacity', value: Math.round(results.maxEMI) },
      { name: 'Disposable Income', value: Math.round(results.disposableIncome) }
    ];
  };

  const generateEligibilityByTenure = () => {
    const income = parseFloat(monthlyIncome) || 0;
    const other = parseFloat(otherIncome) || 0;
    const existing = parseFloat(existingEMI) || 0;
    const rate = parseFloat(interestRate) || 0;

    const totalIncome = income + other;
    const maxFOIR = 0.55;
    const availableForEMI = totalIncome * maxFOIR;
    const maxEMI = availableForEMI - existing;

    const tenures = [5, 10, 15, 20, 25, 30];
    
    return tenures.map(tenure => {
      const monthlyRate = rate / 12 / 100;
      const numPayments = tenure * 12;
      
      let loanAmount = 0;
      if (monthlyRate > 0 && numPayments > 0 && maxEMI > 0) {
        loanAmount = maxEMI * (Math.pow(1 + monthlyRate, numPayments) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, numPayments));
      }

      return {
        tenure: `${tenure}Y`,
        eligibleLoan: Math.round(loanAmount)
      };
    });
  };

  const generateIncomeVsEligibility = () => {
    const existing = parseFloat(existingEMI) || 0;
    const rate = parseFloat(interestRate) || 0;
    const tenure = parseFloat(loanTenure) || 0;

    const incomes = [50000, 75000, 100000, 150000, 200000, 300000];
    
    return incomes.map(income => {
      const maxFOIR = 0.55;
      const availableForEMI = income * maxFOIR;
      const maxEMI = Math.max(0, availableForEMI - existing);

      const monthlyRate = rate / 12 / 100;
      const numPayments = tenure * 12;
      
      let loanAmount = 0;
      if (monthlyRate > 0 && numPayments > 0 && maxEMI > 0) {
        loanAmount = maxEMI * (Math.pow(1 + monthlyRate, numPayments) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, numPayments));
      }

      return {
        income: `$${(income / 1000).toFixed(0)}k`,
        eligibleLoan: Math.round(loanAmount / 100000)
      };
    });
  };

  const getRiskIndicatorData = () => {
    const results = calculateEligibility();
    
    return [
      {
        name: 'FOIR',
        value: results.foirRatio,
        fill: results.foirRatio <= 50 ? 'hsl(var(--secondary))' : results.foirRatio <= 60 ? 'hsl(var(--accent))' : 'hsl(var(--destructive))'
      }
    ];
  };

  const results = calculateEligibility();
  const incomeEMIData = generateIncomeEMIData();
  const eligibilityByTenure = generateEligibilityByTenure();
  const incomeVsEligibility = generateIncomeVsEligibility();
  const riskData = getRiskIndicatorData();

  const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--destructive))', 'hsl(var(--purple))'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getRiskLevel = () => {
    if (results.foirRatio <= 40) return { level: 'Low Risk', color: 'secondary', desc: 'Excellent financial health' };
    if (results.foirRatio <= 50) return { level: 'Moderate Risk', color: 'accent', desc: 'Manageable debt levels' };
    if (results.foirRatio <= 60) return { level: 'High Risk', color: 'destructive', desc: 'High debt burden' };
    return { level: 'Very High Risk', color: 'destructive', desc: 'Excessive debt' };
  };

  const riskLevel = getRiskLevel();

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Loan Eligibility Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is Loan Eligibility?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Loan eligibility determines the maximum loan amount you can borrow based on your income, existing obligations, 
                  age, and other factors. Banks use FOIR (Fixed Obligation to Income Ratio) to assess your repayment capacity.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Your Details</CardTitle>
                  <CardDescription>Enter your financial information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="monthlyIncome" className="text-sm font-medium">
                      Monthly Salary Income ($)
                    </Label>
                    <Input
                      id="monthlyIncome"
                      type="number"
                      value={monthlyIncome}
                      onChange={(e) => setMonthlyIncome(e.target.value)}
                      placeholder="100000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="otherIncome" className="text-sm font-medium">
                      Other Monthly Income ($)
                    </Label>
                    <Input
                      id="otherIncome"
                      type="number"
                      value={otherIncome}
                      onChange={(e) => setOtherIncome(e.target.value)}
                      placeholder="0"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Rental, investments, business income</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="existingEMI" className="text-sm font-medium">
                      Existing Monthly EMIs ($)
                    </Label>
                    <Input
                      id="existingEMI"
                      type="number"
                      value={existingEMI}
                      onChange={(e) => setExistingEMI(e.target.value)}
                      placeholder="15000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">All existing loan EMIs combined</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="age" className="text-sm font-medium">
                      Your Age (Years)
                    </Label>
                    <Input
                      id="age"
                      type="number"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      placeholder="30"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">
                      Max tenure until age 65: {results.maxTenureByAge} years
                    </p>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-foreground">Loan Parameters</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="interestRate" className="text-sm font-medium">
                        Expected Interest Rate (%)
                      </Label>
                      <Input
                        id="interestRate"
                        type="number"
                        value={interestRate}
                        onChange={(e) => setInterestRate(e.target.value)}
                        placeholder="9.5"
                        step="0.1"
                        className="text-base"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="loanTenure" className="text-sm font-medium">
                        Desired Loan Tenure (Years)
                      </Label>
                      <Input
                        id="loanTenure"
                        type="number"
                        value={loanTenure}
                        onChange={(e) => setLoanTenure(e.target.value)}
                        placeholder="20"
                        className="text-base"
                      />
                    </div>
                  </div>

                  <Button 
                    className="w-full gradient-primary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setMonthlyIncome(monthlyIncome)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate Eligibility
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Your Loan Eligibility</CardTitle>
                    <CardDescription>Maximum loan you can get approved for</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Max Eligible Loan</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(results.maxLoanAmount)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Max Monthly EMI</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(results.maxEMI)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">FOIR Ratio</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent">
                          {results.foirRatio.toFixed(1)}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Fixed Obligation to Income</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-purple-light to-card border border-purple/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-purple flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">DTI Ratio</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-purple">
                          {results.dtiRatio.toFixed(1)}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Current Debt-to-Income</p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className={`p-6 rounded-xl bg-gradient-to-br from-${riskLevel.color}-light to-card border border-${riskLevel.color}/20 border`}>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-medium">Risk Assessment:</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold bg-${riskLevel.color}/20 text-${riskLevel.color}`}>
                          {riskLevel.level}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        {riskLevel.desc}. Your FOIR is {results.foirRatio.toFixed(1)}%, {results.foirRatio <= 50 ? 'well within' : 'approaching'} safe lending limits.
                      </p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground mb-1">Disposable Income</p>
                          <p className="font-semibold text-foreground">{formatCurrency(results.disposableIncome)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground mb-1">Total Interest</p>
                          <p className="font-semibold text-foreground">{formatCurrency(results.totalInterest)}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Income vs EMI Capacity</CardTitle>
                <CardDescription className="text-base">
                  Breakdown of your monthly income allocation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={incomeEMIData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        type="number"
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <YAxis 
                        type="category"
                        dataKey="name" 
                        stroke="hsl(var(--muted-foreground))"
                        width={150}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Bar dataKey="value" name="Amount">
                        {incomeEMIData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Eligibility by Loan Tenure</CardTitle>
                  <CardDescription>
                    How tenure affects your eligible loan amount
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={eligibilityByTenure}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="tenure" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 100000).toFixed(0)}L`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="eligibleLoan" fill="hsl(var(--primary))" name="Eligible Loan Amount" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">FOIR Risk Indicator</CardTitle>
                  <CardDescription>
                    Your debt burden assessment
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px] flex items-center justify-center">
                    <div className="text-center">
                      <ResponsiveContainer width={300} height={250}>
                        <RadialBarChart 
                          cx="50%" 
                          cy="50%" 
                          innerRadius="60%" 
                          outerRadius="100%" 
                          data={riskData}
                          startAngle={180}
                          endAngle={0}
                        >
                          <RadialBar
                            minAngle={15}
                            background
                            clockWise={true}
                            dataKey="value"
                          />
                        </RadialBarChart>
                      </ResponsiveContainer>
                      <div className="mt-4">
                        <p className="text-4xl font-display font-bold text-foreground">
                          {results.foirRatio.toFixed(1)}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">
                          FOIR (Target: &lt;50%)
                        </p>
                        <p className={`text-sm font-semibold mt-2 text-${riskLevel.color}`}>
                          {riskLevel.level}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Income vs Eligibility Curve</CardTitle>
                <CardDescription className="text-base">
                  How different income levels affect loan eligibility
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={incomeVsEligibility}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="income" 
                        stroke="hsl(var(--muted-foreground))"
                        label={{ value: 'Monthly Income', position: 'insideBottom', offset: -5 }}
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        label={{ value: 'Eligible Loan ($ Lakhs)', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => `$${value} Lakhs`}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="eligibleLoan" 
                        stroke="hsl(var(--primary))" 
                        strokeWidth={3}
                        name="Eligible Loan"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Loan Eligibility</CardTitle>
                <CardDescription className="text-base">
                  Learn how banks assess your loan application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    Your Eligibility Assessment
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Based on your monthly income of <strong className="text-primary">{formatCurrency(parseFloat(monthlyIncome) + parseFloat(otherIncome))}</strong> and 
                    existing EMIs of <strong className="text-primary">{formatCurrency(parseFloat(existingEMI))}</strong>, 
                    you are eligible for a maximum loan of <strong className="text-primary">{formatCurrency(results.maxLoanAmount)}</strong>.
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm space-y-2">
                    <p className="font-mono">
                      <strong>Available for EMI:</strong> {formatCurrency(results.availableForEMI)} (55% of income)
                    </p>
                    <p className="font-mono">
                      <strong>Less Existing EMI:</strong> {formatCurrency(parseFloat(existingEMI))}
                    </p>
                    <p className="font-mono">
                      <strong>Max New EMI:</strong> {formatCurrency(results.maxEMI)}
                    </p>
                    <p className="font-mono">
                      <strong>Max Loan Amount:</strong> {formatCurrency(results.maxLoanAmount)}
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    FOIR & DTI Explained
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Banks use two key metrics to assess your repayment capacity:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-secondary-light border border-secondary/20">
                      <h4 className="font-semibold text-foreground mb-2">FOIR (Fixed Obligation to Income Ratio)</h4>
                      <p className="text-3xl font-bold text-secondary mb-2">{results.foirRatio.toFixed(1)}%</p>
                      <p className="text-sm text-muted-foreground mb-2">
                        Measures total EMIs (existing + new) as % of income
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Safe limit: 50% | Your value: {results.foirRatio.toFixed(1)}%
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-light border border-purple/20">
                      <h4 className="font-semibold text-foreground mb-2">DTI (Debt-to-Income Ratio)</h4>
                      <p className="text-3xl font-bold text-purple mb-2">{results.dtiRatio.toFixed(1)}%</p>
                      <p className="text-sm text-muted-foreground mb-2">
                        Measures existing EMIs as % of income
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Safe limit: 30% | Your value: {results.dtiRatio.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    How to Improve Your Eligibility
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">💰 Increase Your Income</h4>
                      <p className="text-muted-foreground text-sm">
                        Include all sources: salary, rental income, investment returns. Co-applicant income can significantly boost eligibility.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">📉 Reduce Existing EMIs</h4>
                      <p className="text-muted-foreground text-sm">
                        Pay off smaller loans, consolidate debts, or restructure existing loans to lower monthly obligations.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">📈 Improve Credit Score</h4>
                      <p className="text-muted-foreground text-sm">
                        Maintain 750+ CIBIL score for best rates and higher eligibility. Pay all EMIs on time, keep credit utilization low.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-light border-l-4 border-purple">
                      <h4 className="font-semibold text-foreground mb-2">⏳ Opt for Longer Tenure</h4>
                      <p className="text-muted-foreground text-sm">
                        Longer tenure reduces EMI, increasing eligibility. But remember - more interest over loan lifetime.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Factors Banks Consider
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-primary font-semibold flex-shrink-0">✓</span>
                      <div>
                        <strong className="text-foreground">Age & Employment:</strong> Younger applicants get longer tenure. 
                        Stable employment (2+ years) increases approval chances. Salaried employees from reputed firms get better terms.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-primary font-semibold flex-shrink-0">✓</span>
                      <div>
                        <strong className="text-foreground">Credit History:</strong> CIBIL score is crucial. 750+ is excellent, 
                        650-750 is good, below 650 may face rejection or higher rates.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-primary font-semibold flex-shrink-0">✓</span>
                      <div>
                        <strong className="text-foreground">Existing Obligations:</strong> All existing loans, credit card EMIs, 
                        and other liabilities reduce your eligibility proportionally.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-primary font-semibold flex-shrink-0">✓</span>
                      <div>
                        <strong className="text-foreground">Property Value & LTV:</strong> Banks typically finance 75-90% of property value. 
                        Higher down payment improves eligibility and reduces interest rates.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Common Eligibility Mistakes
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">❌</span>
                      <div>
                        <strong className="text-foreground">Not including co-applicant income:</strong> Spouse or family income 
                        can significantly boost eligibility without additional liabilities.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">❌</span>
                      <div>
                        <strong className="text-foreground">Applying with multiple banks simultaneously:</strong> Multiple credit 
                        inquiries hurt credit score. Research and shortlist 2-3 banks first.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">❌</span>
                      <div>
                        <strong className="text-foreground">Ignoring hidden costs:</strong> Processing fees, insurance, 
                        legal charges add 2-3% to loan amount. Factor these in your planning.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">❌</span>
                      <div>
                        <strong className="text-foreground">Maxing out eligibility:</strong> Just because you're eligible doesn't 
                        mean you should borrow the maximum. Leave room for emergencies and lifestyle.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    Next Steps After Eligibility Check
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">1️⃣ Check Your Credit Report</h4>
                      <p className="text-muted-foreground text-sm">
                        Get free credit report from CIBIL, Experian, or CRIF. Verify all information is accurate.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">2️⃣ Compare Bank Offers</h4>
                      <p className="text-muted-foreground text-sm">
                        Get quotes from 3-4 banks. Compare interest rates, processing fees, prepayment charges, and flexibility.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">3️⃣ Prepare Documents</h4>
                      <p className="text-muted-foreground text-sm">
                        ID proof, address proof, income proof (3-6 months salary slips, IT returns), bank statements, property documents.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-light border-l-4 border-purple">
                      <h4 className="font-semibold text-foreground mb-2">4️⃣ Plan Your Finances</h4>
                      <p className="text-muted-foreground text-sm">
                        Ensure 3-6 months emergency fund, budget for additional costs, maintain post-EMI savings of 20%+ for long-term goals.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default LoanEligibilityCalculator;
